// Sentry init must be the first runtime statement so it catches early errors.
import { initSentry } from "./lib/sentry-init";
initSentry();

import React, { useMemo } from "react";
import ReactDOM from "react-dom/client";

// Mantine imports
import { MantineProvider } from "@mantine/core";
import { ModalsProvider } from "@mantine/modals";
import { Notifications } from "@mantine/notifications";

import { createBrowserRouter, RouterProvider } from "react-router-dom";

import App from "./App";
import { appRouterOptions, appRoutes } from "@/routing/appRouter";
import { RethrowRouteError } from "@/routing/RethrowRouteError";
import { AppErrorBoundary } from "@/components/errors/AppErrorBoundary";
import { triggerChunkReload } from "@/lib/chunkReload";
import { buildThemeFromBranding } from "./theme";
import {
  BrandingProvider,
  BrandingFallbackBanner,
  useBranding,
} from "@/context/BrandingContext";
import { semanticColors, accent, errorColor, successColor, warningColor } from "@/ui/tokens";

import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import "@mantine/notifications/styles.css";
import "@mantine/code-highlight/styles.css";
import "./styles/verity-theme.css";
import "./styles/dark-theme.css";
import {
  CodeHighlightAdapterProvider,
  createHighlightJsAdapter,
} from "@mantine/code-highlight";
import hljs from "highlight.js/lib/core";
import sqlLang from "highlight.js/lib/languages/sql";
import typescriptLang from "highlight.js/lib/languages/typescript";
import jsonLang from "highlight.js/lib/languages/json";
import bashLang from "highlight.js/lib/languages/bash";
import pythonLang from "highlight.js/lib/languages/python";
import plaintextLang from "highlight.js/lib/languages/plaintext";
hljs.registerLanguage("sql", sqlLang);
// Used by docs articles (apps/frontend/docs/**) for code samples — see
// docs/plans-pending/2026-05-10-kb-article-kv-export.md. CodeHighlight
// falls back to "plaintext" when the fence has no language tag, so
// register that too to avoid the renderer throwing.
hljs.registerLanguage("typescript", typescriptLang);
hljs.registerLanguage("ts", typescriptLang);
hljs.registerLanguage("json", jsonLang);
hljs.registerLanguage("bash", bashLang);
hljs.registerLanguage("sh", bashLang);
hljs.registerLanguage("shell", bashLang);
hljs.registerLanguage("python", pythonLang);
hljs.registerLanguage("plaintext", plaintextLang);
import "@mantine/code-highlight/styles.css";
import "highlight.js/styles/github.min.css";

const highlightJsAdapter = createHighlightJsAdapter(hljs);

// Vite's structural signal for a stale dynamic-import chunk (the classic
// white-screen-after-deploy). Handle it as the PRIMARY trigger: prevent Vite
// from throwing and run the same guarded one-time reload the error boundary
// uses. Anything this misses still falls through to <AppErrorBoundary>.
window.addEventListener("vite:preloadError", (event) => {
  event.preventDefault();
  triggerChunkReload();
});

const light = semanticColors.light;
const dark = semanticColors.dark;

// Extracted to a module const so the per-partner MantineProvider (BrandedRoot)
// can reuse it unchanged — the legacy `--app-*` / `--verity-*` tokens stay
// identical across all partners; only `primaryColor` swaps per brand.
const cssVariablesResolver = () => ({
        variables: {
          // Shared across both schemes
          "--verity-accent": accent,
          "--verity-error": errorColor,
          "--verity-success": successColor,
          "--verity-warning": warningColor,
        },
        light: {
          // Legacy vars (kept for backward compat during migration)
          "--app-bg": light.pageBg,
          "--app-header-bg": light.headerBg,
          "--app-sidebar-bg": light.sidebarBg,
          "--app-border": light.border,
          "--app-text": light.textPrimary,
          "--app-text-secondary": light.textSecondary,
          "--app-card-bg": light.cardBg,
          "--app-card-border": light.cardBorder,
          "--app-card-shadow": light.cardShadow,
          "--app-glass-blur": light.glassBlur,
          // New verity tokens
          "--verity-surface-bg": light.surfaceBg,
          "--verity-surface-border": light.surfaceBorder,
          "--verity-page-bg": light.pageBg,
          "--verity-header-bg": light.headerBg,
          "--verity-sidebar-bg": light.sidebarBg,
          "--verity-text-primary": light.textPrimary,
          "--verity-text-secondary": light.textSecondary,
          "--verity-text-muted": light.textMuted,
          "--verity-border": light.border,
          "--verity-card-bg": light.cardBg,
          "--verity-card-border": light.cardBorder,
          "--verity-card-shadow": light.cardShadow,
          "--verity-input-bg": light.inputBg,
          "--verity-input-border": light.inputBorder,
          "--verity-input-border-hover": light.inputBorderHover,
          "--verity-input-shadow": light.inputShadow,
          "--verity-focus-ring": light.focusRing,
          "--verity-focus-border": light.focusBorder,
          "--verity-dropdown-bg": light.dropdownBg,
          "--verity-dropdown-border": light.dropdownBorder,
          "--verity-dropdown-shadow": light.dropdownShadow,
          "--verity-hover-bg": light.hoverBg,
          "--verity-label-color": light.labelColor,
          "--verity-placeholder-color": light.placeholderColor,
          "--verity-disabled-bg": light.disabledBg,
          "--verity-disabled-border": light.disabledBorder,
          "--verity-disabled-text": light.disabledText,
          "--verity-glass-blur": light.glassBlur ?? "none",
        },
        dark: {
          // Legacy vars (kept for backward compat during migration)
          "--app-bg": dark.pageBgGradient,
          "--app-bg-solid": dark.pageBg,
          "--app-header-bg": dark.headerBg,
          "--app-sidebar-bg": dark.sidebarBg,
          "--app-border": dark.border,
          "--app-text": dark.textPrimary,
          "--app-text-secondary": dark.textSecondary,
          "--app-card-bg": dark.cardBg,
          "--app-card-border": dark.cardBorder,
          "--app-card-shadow": dark.cardShadow,
          "--app-glass-blur": dark.glassBlur,
          // New verity tokens
          "--verity-surface-bg": dark.surfaceBg,
          "--verity-surface-border": dark.surfaceBorder,
          "--verity-page-bg": dark.pageBg,
          "--verity-page-bg-gradient": dark.pageBgGradient,
          "--verity-header-bg": dark.headerBg,
          "--verity-sidebar-bg": dark.sidebarBg,
          "--verity-text-primary": dark.textPrimary,
          "--verity-text-secondary": dark.textSecondary,
          "--verity-text-muted": dark.textMuted,
          "--verity-border": dark.border,
          "--verity-card-bg": dark.cardBg,
          "--verity-card-border": dark.cardBorder,
          "--verity-card-shadow": dark.cardShadow,
          "--verity-input-bg": dark.inputBg,
          "--verity-input-border": dark.inputBorder,
          "--verity-input-border-hover": dark.inputBorderHover,
          "--verity-input-shadow": dark.inputShadow,
          "--verity-focus-ring": dark.focusRing,
          "--verity-focus-border": dark.focusBorder,
          "--verity-dropdown-bg": dark.dropdownBg,
          "--verity-dropdown-border": dark.dropdownBorder,
          "--verity-dropdown-shadow": dark.dropdownShadow,
          "--verity-hover-bg": dark.hoverBg,
          "--verity-label-color": dark.labelColor,
          "--verity-placeholder-color": dark.placeholderColor,
          "--verity-disabled-bg": dark.disabledBg,
          "--verity-disabled-border": dark.disabledBorder,
          "--verity-disabled-text": dark.disabledText,
          "--verity-glass-blur": dark.glassBlur,
        },
  });

function BrandedRoot() {
  const { branding } = useBranding();
  // Rebuild the Mantine theme only when the partner's primary color changes,
  // so the provider re-renders per partner without thrashing on every state tick.
  const dynamicTheme = useMemo(
    () => buildThemeFromBranding(branding),
    [branding.primaryColor]
  );
  return (
    <MantineProvider
      theme={dynamicTheme}
      defaultColorScheme="light"
      cssVariablesResolver={cssVariablesResolver}
    >
      <CodeHighlightAdapterProvider adapter={highlightJsAdapter}>
        {/* House look for every modals.open* dialog (plan
            2026-10-08-one-modal-component §2). No zIndex here: the provider
            sets its own default + 1, and this object is spread over it. */}
        <ModalsProvider modalProps={{ radius: "lg", centered: true }}>
          <Notifications position="top-right" />
          <BrandingFallbackBanner />
          <RouterProvider router={router} onError={silenceRethrownRouteError} />
        </ModalsProvider>
      </CodeHighlightAdapterProvider>
    </MantineProvider>
  );
}

// One router per page load (ADR-0386). Its single splat route renders the
// whole app; `RethrowRouteError` hands render errors to the boundary below.
const router = createBrowserRouter(
  appRoutes(<App />, <RethrowRouteError />),
  appRouterOptions,
);

// react-router logs every error its route boundary catches. `RethrowRouteError`
// passes each one on to <AppErrorBoundary>, which reports it to Sentry, so the
// router's own log line would only be a duplicate.
function silenceRethrownRouteError(): void {}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <AppErrorBoundary variant="inline">
      <BrandingProvider>
        <BrandedRoot />
      </BrandingProvider>
    </AppErrorBoundary>
  </React.StrictMode>
);
